// P2-7 — THE GESTURE ITSELF. One mount, at the app shell, for the whole site.
//
// The LAW is `src/pullToRefresh.js` (the physics, the eligibility rule and the registry) and it is
// RUN by `scripts/p27-pull-refresh.mjs`. What is here is the part that needs a browser: three touch
// handlers and an indicator. Nothing in this file decides anything a gate cannot reach.
//
// **WHY THIS MOUNTS ONCE AND NOT PER SCREEN:** her order says the refresh must not be built surface
// by surface. One gesture, one registry, and a surface joins with `useRefresh`. A screen that has
// not joined refreshes nothing and says nothing — which is honest, and is why the gate asserts that
// every feed-style surface has actually joined.
import { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import {
  PULL_SLOP, PULL_THRESHOLD, PULL_COPY, canStartPull, pullDistance, pullProgress,
  pullState, registerRefresh, runRefresh, pullOutcome, needsFullReload,
} from "../pullToRefresh.js";
import { applyUpdate } from "../lib/pwa";

// **THE ELEMENT THAT ACTUALLY SCROLLS, RESOLVED FROM THE TOUCH ITSELF.** Her trace's first question,
// and it is the one a Chrome harness cannot ask: an installed iOS app may scroll a wrapper rather
// than the document, and a gesture that reads `window.scrollY` while a div is the scroller sees 0
// forever — so it either never arms or arms in the middle of the page. Walking up from what she
// actually touched answers it on ANY architecture rather than assuming one.
function scrollerFor(node: Element | null): { el: Element | Window; top: number } {
  let el: Element | null = node;
  while (el && el !== document.body && el !== document.documentElement) {
    const style = window.getComputedStyle(el);
    const scrolls = /(auto|scroll)/.test(`${style.overflowY}${style.overflow}`);
    if (scrolls && el.scrollHeight > el.clientHeight + 1) return { el, top: el.scrollTop };
    el = el.parentElement;
  }
  const doc = document.scrollingElement || document.documentElement;
  // `window.scrollY` and `scrollingElement.scrollTop` agree in Chrome; on iOS the rubber band shows
  // up in one of them first, so the SMALLER (most negative) is the honest reading of "at the top".
  return { el: window, top: Math.min(doc ? doc.scrollTop : 0, window.scrollY) };
}

// A SURFACE JOINS BY CALLING THIS. The key is the surface's own name so a remount replaces its
// handler rather than adding a second one, and the effect unregisters on unmount, so a screen she
// has left never re-fetches behind her.
export function useRefresh(key: string, fn: () => void | Promise<unknown>) {
  const latest = useRef(fn);
  latest.current = fn;
  useEffect(() => registerRefresh(key, () => latest.current()), [key]);
}

export function PullToRefresh({ waitingUpdate = false }: { waitingUpdate?: boolean }) {
  const [distance, setDistance] = useState(0);
  const [busy, setBusy] = useState<"" | "refreshing" | "updating">("");
  const start = useRef<number | null>(null);
  const travelled = useRef(0);

  const finish = useCallback(async () => {
    setBusy("refreshing");
    setDistance(PULL_THRESHOLD);
    try {
      const { ran } = await runRefresh();
      // HER SECOND SYMPTOM, ANSWERED STRUCTURALLY: if the screen she is standing on never joined the
      // registry, `runRefresh` does nothing and the spinner still turns — the indicator appears and
      // the content does not update. Reloading is what a pull MEANS in an installed app with no
      // browser chrome, so a zero-handler refresh reloads rather than lying.
      if (needsFullReload(ran)) { window.location.reload(); return; }
      // P2-7c: the update is applied AFTER the data refresh, never instead of it — applying reloads
      // the page, so doing it first throws away the re-fetch she asked for.
      if (pullOutcome({ waitingWorker: waitingUpdate }) === "refresh-then-update") {
        setBusy("updating");
        applyUpdate();
        return;                       // the page is about to reload; do not clear the indicator
      }
    } finally {
      if (pullOutcome({ waitingWorker: waitingUpdate }) !== "refresh-then-update") {
        setBusy("");
        setDistance(0);
        travelled.current = 0;
      }
    }
  }, [waitingUpdate]);

  useEffect(() => {
    const onStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      // THE ELIGIBILITY QUESTION IS ASKED ONCE, AT TOUCH START, and asked of the LAW. Re-asking it
      // mid-drag would arm the gesture the moment a bouncy scroll passed through zero.
      const el = e.target as HTMLElement | null;
      const interactive = Boolean(el?.closest("input, textarea, select, [contenteditable], .lr-rail, [data-no-pull]"));
      const scroller = scrollerFor(el);
      if (!canStartPull({ scrollTop: scroller.top, width: window.innerWidth, touch: true, busy: Boolean(busy), interactive })) {
        start.current = null;
        return;
      }
      start.current = t.clientY;
    };

    const onMove = (e: TouchEvent) => {
      if (start.current === null) return;
      const t = e.touches[0];
      if (!t) return;
      const raw = t.clientY - start.current;
      if (raw < PULL_SLOP) { setDistance(0); travelled.current = 0; return; }
      // **THE PAGE MUST NOT ALSO SCROLL while the indicator is out**, or the content slides under a
      // spinner and the gesture reads as a rendering fault. `touch-action: none` on the shell would
      // kill ordinary scrolling everywhere, so it is refused here, only while armed, and only when
      // the event is cancelable.
      if (e.cancelable) e.preventDefault();
      travelled.current = pullDistance(raw);
      setDistance(travelled.current);
    };

    const onEnd = () => {
      if (start.current === null) return;
      start.current = null;
      // THE DECISION IS READ OFF A REF, NOT OUT OF A STATE UPDATER. Firing the refresh inside
      // `setDistance(d => …)` worked, and a state updater is required to be pure — React is free to
      // call it twice, which is a second refresh and a second set of vendor calls.
      if (pullState(travelled.current) === "ready") { void finish(); return; }
      setDistance(0);                 // SNAP BACK. A pull that did not reach the threshold does nothing.
      travelled.current = 0;
    };

    // **ON `document`, IN THE CAPTURE PHASE.** A bubble-phase listener on `window` is the last thing
    // to hear a touch, so anything in between that calls `stopPropagation` silences the gesture
    // entirely — "nothing happens when I pull", with nothing thrown and nothing logged. Capture on
    // the document hears it FIRST and cannot be talked out of it by a child. Measured: no touch
    // handler in this app stops propagation today, which is precisely why relying on that would be a
    // promise about code nobody has written yet rather than a property of this one.
    const opts = { capture: true } as const;
    document.addEventListener("touchstart", onStart, { ...opts, passive: true });
    document.addEventListener("touchmove", onMove, { ...opts, passive: false });
    document.addEventListener("touchend", onEnd, { ...opts, passive: true });
    document.addEventListener("touchcancel", onEnd, { ...opts, passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart, opts);
      document.removeEventListener("touchmove", onMove, opts);
      document.removeEventListener("touchend", onEnd, opts);
      document.removeEventListener("touchcancel", onEnd, opts);
    };
  }, [busy, finish]);

  if (!distance && !busy) return null;

  const state = busy || pullState(distance);
  const label = busy === "updating" ? PULL_COPY.updating
    : busy === "refreshing" ? PULL_COPY.refreshing
      : state === "ready" ? PULL_COPY.ready : PULL_COPY.pulling;

  return (
    <div className="pull-refresh" data-state={state} aria-hidden={busy ? undefined : true}
      style={{ transform: `translateY(${Math.round(distance)}px)` }}>
      <span className="pull-refresh__disc" style={{ opacity: 0.35 + pullProgress(distance) * 0.65 }}>
        <RefreshCw size={18} style={{ transform: `rotate(${Math.round(pullProgress(distance) * 270)}deg)` }} />
      </span>
      <span className="pull-refresh__say" role={busy ? "status" : undefined}>{label}</span>
    </div>
  );
}
